import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ConfigDto,
  EditActionDto,
  NewTransactionDto,
  TransactionUpdateDto,
  GetMonthlySummaryQueryDto,
  MonthlySummaryDto,
  MonthlyPendingSummaryDto,
  MatchProblemSummaryDto,
  GetTransactionsQueryDto,
  TransactionPageDto,
  TransactionDetailsDto,
  TransactionPreviewDto,
  AutoTransactionRuleDto,
  RebuildStatusDto,
  ServerStatusDto,
  ImportItem,
  ImportSort,
  PendingImportFileDto,
  UpdateImportDto,
} from "./types";
import {
  deleteAutoTransactionRule,
  getAutoTransactionRules,
  updateAutoTransactionRule,
} from "./auto-transactions";
import type { UpdateAutoTransactionRuleDto } from "./types";
import {
  createActionSet,
  createMatchSet,
  deleteActionSet,
  deleteMatchSet,
  getActionSets,
  getMatchSets,
  updateActionSet,
  updateMatchSet,
} from "./library";
import type {
  ActionSetDto,
  MatchSetDto,
  NewActionSetDto,
  NewMatchSetDto,
  UpdateActionSetDto,
  UpdateMatchSetDto,
} from "./types";
import { z } from "zod";
import {
  MatchProblemSummaryDtoSchema,
  MonthlyPendingSummaryDtoSchema,
  TransactionPageDtoSchema,
  TransactionDetailsDtoSchema,
  MonthlySummaryDtoSchema,
  TransactionPreviewDtoSchema,
  ConfigDtoSchema,
  RebuildStatusDtoSchema,
  ServerStatusDtoSchema,
  ImportItemSchema,
  ImportDiffDtoSchema,
  ImportResultDtoSchema,
  PendingImportFileDtoSchema,
} from "./types";
import {
  handleResponse,
  API_BASE,
  isClientError,
  parseResponse,
} from "./common";
import { fetchWithInterceptor } from "./client";

const buildTransactionFilterParams = (
  filters: GetTransactionsQueryDto | undefined,
  { includePagination }: { includePagination: boolean },
): URLSearchParams => {
  const params = new URLSearchParams();
  if (!filters) return params;

  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  filters.source?.forEach((s) => params.append("source", s));
  filters.expenseType?.forEach((e) => params.append("expenseType", e));
  filters.group?.forEach((g) => params.append("group", g));
  filters.category?.forEach((c) => params.append("category", c));
  if (filters.merchant) params.append("merchant", filters.merchant);
  if (filters.description) params.append("description", filters.description);
  filters.autoTransactionId?.forEach((id) =>
    params.append("autoTransactionId", id),
  );
  if (filters.importId) params.append("importId", filters.importId);
  filters.invoice?.forEach((id) => params.append("invoice", id));
  if (filters.ignored) params.append("ignored", "true");
  if (filters.matchProblem) params.append("matchProblem", "true");
  if (filters.sort) params.append("sort", filters.sort);
  if (includePagination) {
    if (filters.limit) params.append("limit", filters.limit.toString());
    if (filters.cursor) params.append("cursor", filters.cursor);
  }
  return params;
};

/**
 * Up-to-`limit` calendar months that still have matching pending transactions,
 * most recent first. Drives the Home page's pending inbox: it walks backwards
 * from this month and only returns months that actually have rows, so empty
 * months are skipped transparently.
 *
 * Filter shape mirrors `useTransactions` — the pagination-related fields
 * (`limit`, `cursor`, `sort`) are ignored server-side; only the filter half
 * matters.
 */
export const usePendingMonthSummary = (
  filters: GetTransactionsQueryDto | undefined,
  limit = 2,
) => {
  return useQuery<MonthlyPendingSummaryDto>({
    queryKey: ["pending-month-summary", filters, limit],
    queryFn: async () => {
      const params = buildTransactionFilterParams(filters, {
        includePagination: false,
      });
      params.set("limit", limit.toString());
      const url = `${API_BASE}/transactions/month-summary?${params.toString()}`;
      const response = await fetchWithInterceptor(url);
      await handleResponse(response);
      return parseResponse(
        MonthlyPendingSummaryDtoSchema,
        await response.json(),
        "GET /transactions/month-summary",
      );
    },
  });
};

/**
 * One page of transactions. `nextCursor` feeds back in as `filters.cursor` to
 * fetch the page after this one; `hasMore` says whether there is one.
 */
export const useTransactions = (filters?: GetTransactionsQueryDto) => {
  return useQuery<TransactionPageDto>({
    queryKey: ["transactions", filters],
    queryFn: async () => {
      const params = buildTransactionFilterParams(filters, {
        includePagination: true,
      });
      const queryString = params.toString();
      const url = queryString
        ? `${API_BASE}/transactions?${queryString}`
        : `${API_BASE}/transactions`;

      const response = await fetchWithInterceptor(url);
      await handleResponse(response);
      return parseResponse(
        TransactionPageDtoSchema,
        await response.json(),
        "GET /transactions",
      );
    },
    // Keeping the previous page on screen while the next one loads stops the
    // table from collapsing to a spinner on every Next/Previous click.
    placeholderData: (previous) => previous,
  });
};

export const downloadTransactionsCsv = async (
  filters: GetTransactionsQueryDto,
): Promise<void> => {
  const params = buildTransactionFilterParams(filters, {
    includePagination: false,
  });
  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE}/transactions/export?${queryString}`
    : `${API_BASE}/transactions/export`;

  const response = await fetchWithInterceptor(url);
  await handleResponse(response);

  const blob = await response.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(downloadUrl);
};

export const useResetTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/transactions/${id}/reset`,
        {
          method: "POST",
        },
      );
      await handleResponse(response);
      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["transactionDetails", id] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["monthlySummary"] });
      queryClient.invalidateQueries({ queryKey: ["serverStatus"] });
    },
  });
};

/**
 * Which rules hit a `match` they couldn't dispatch, and on how many
 * transactions. A rule with a hole in it is a rule with a bug, and this is the
 * only place that says so across the whole ledger — the transaction page only
 * ever speaks for the one transaction you have open.
 */
export const useMatchProblemSummary = () => {
  return useQuery<MatchProblemSummaryDto>({
    queryKey: ["matchProblems"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/transactions/match-problems`,
      );
      await handleResponse(response);
      return parseResponse(
        MatchProblemSummaryDtoSchema,
        await response.json(),
        "GET /transactions/match-problems",
      );
    },
  });
};

export const useAutoTransactionRules = () => {
  return useQuery<AutoTransactionRuleDto[]>({
    queryKey: ["autoTransactionRules"],
    queryFn: getAutoTransactionRules,
  });
};

export const useDeleteAutoTransactionRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAutoTransactionRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["autoTransactionRules"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["matchProblems"] });
    },
  });
};

/**
 * PUT a rule. Used by the admin list for inline edits (enable toggle, drag
 * reorder, bulk enable/disable). The form page calls the bare
 * `updateAutoTransactionRule` directly so it can sequence its own error
 * handling.
 */
export const useUpdateAutoTransactionRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      rule,
    }: {
      id: string;
      rule: UpdateAutoTransactionRuleDto;
    }) => updateAutoTransactionRule(id, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["autoTransactionRules"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["matchProblems"] });
    },
  });
};

export const useTransactionDetails = (id: string | undefined) => {
  return useQuery<TransactionDetailsDto>({
    queryKey: ["transactionDetails", id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      const response = await fetchWithInterceptor(
        `${API_BASE}/transactions/${id}/details`,
      );
      await handleResponse(response);
      return parseResponse(
        TransactionDetailsDtoSchema,
        await response.json(),
        "GET /transactions/:id/details",
      );
    },
    enabled: !!id,
  });
};

export const useMonthlySummary = (
  params: GetMonthlySummaryQueryDto,
  options?: Omit<UseQueryOptions<MonthlySummaryDto>, "queryKey" | "queryFn">,
) => {
  return useQuery<MonthlySummaryDto>({
    queryKey: ["monthlySummary", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (params.type === "month") {
        queryParams.append("type", "month");
        queryParams.append("year", params.year.toString());
        queryParams.append("month", params.month.toString());
        if (params.source) {
          queryParams.append("source", params.source);
        }
      } else if (params.type === "invoice") {
        queryParams.append("type", "invoice");
        queryParams.append("invoiceId", params.invoiceId);
      }

      const response = await fetchWithInterceptor(
        `${API_BASE}/summary?${queryParams.toString()}`,
      );
      await handleResponse(response);
      return parseResponse(
        MonthlySummaryDtoSchema,
        await response.json(),
        "GET /summary",
      );
    },
    ...options,
  });
};

export const usePreviewTransactionUpdate = () => {
  return useMutation({
    mutationFn: async (params: {
      id: string;
      actions: EditActionDto[];
    }): Promise<TransactionPreviewDto> => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/transactions/${params.id}/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ actions: params.actions }),
        },
      );
      await handleResponse(response);
      return parseResponse(
        TransactionPreviewDtoSchema,
        await response.json(),
        "POST /transactions/:id/preview",
      );
    },
  });
};

export const useBulkUpdateTransactions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: TransactionUpdateDto[]) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/transactions/bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        },
      );
      await handleResponse(response);
      if (
        response.status === 204 ||
        response.headers.get("Content-Length") === "0"
      ) {
        return;
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["monthlySummary"] });
      queryClient.invalidateQueries({ queryKey: ["serverStatus"] });
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
      // Applying a rule is exactly how a fall-through appears, and resetting
      // one is how it goes away.
      queryClient.invalidateQueries({ queryKey: ["matchProblems"] });
      variables.forEach((update) => {
        queryClient.invalidateQueries({
          queryKey: ["transactionDetails", update.id],
        });
      });
    },
  });
};

export const useConfig = () => {
  return useQuery<ConfigDto>({
    queryKey: ["config"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(`${API_BASE}/config`);
      await handleResponse(response);
      return parseResponse(
        ConfigDtoSchema,
        await response.json(),
        "GET /config",
      );
    },
  });
};

export const useCreateTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transaction: NewTransactionDto) => {
      const response = await fetchWithInterceptor(`${API_BASE}/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(transaction),
      });
      await handleResponse(response);
      if (
        response.status === 201 ||
        response.headers.get("Content-Length") === "0"
      ) {
        return;
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["monthlySummary"] });
      queryClient.invalidateQueries({ queryKey: ["serverStatus"] });
    },
  });
};

export const useRebuildIndex = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/admin/rebuild-index`,
        {
          method: "POST",
        },
      );
      await handleResponse(response);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rebuildStatus"] });
      queryClient.invalidateQueries();
    },
  });
};

export const useRebuildStatus = (enabled = true) => {
  return useQuery<RebuildStatusDto>({
    queryKey: ["rebuildStatus"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/admin/rebuild-status`,
      );
      await handleResponse(response);
      return parseResponse(
        RebuildStatusDtoSchema,
        await response.json(),
        "GET /admin/rebuild-status",
      );
    },
    enabled,
    refetchInterval: (query) => (query.state.data?.isRebuilding ? 2000 : 30000),
  });
};

export const useServerStatus = () => {
  return useQuery<ServerStatusDto>({
    queryKey: ["serverStatus"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(`${API_BASE}/status`);
      await handleResponse(response);
      return parseResponse(
        ServerStatusDtoSchema,
        await response.json(),
        "GET /status",
      );
    },
    refetchInterval: (query) =>
      query.state.data?.rebuildStatus.isRebuilding ? 2000 : false,
  });
};

export const useImports = (sort: ImportSort = "uploadDateDesc") => {
  return useQuery<ImportItem[]>({
    queryKey: ["imports", sort],
    queryFn: async () => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports?sort=${sort}`,
      );
      await handleResponse(res);
      return parseResponse(
        z.array(ImportItemSchema),
        await res.json(),
        "GET /imports",
      );
    },
  });
};

const invalidateImportAndDownstream = (
  queryClient: ReturnType<typeof useQueryClient>,
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ["imports"] }),
    queryClient.invalidateQueries({ queryKey: ["transactions"] }),
    queryClient.invalidateQueries({ queryKey: ["monthlySummary"] }),
    queryClient.invalidateQueries({ queryKey: ["transactionDetails"] }),
  ]);

/**
 * Re-run / upload / discard only touch pending diffs, not applied transactions,
 * so they don't need the downstream (transactions / summary) invalidation that
 * apply does. But they DO change what the imports list shows — the pending-diff
 * badge, the `needsRerun` flag, the date range — so the list query must refetch,
 * not just the per-import pending-diffs sub-query. Invalidating the `["imports"]`
 * root refetches the list AND (by prefix match) every `["imports", id, …]`
 * sub-query, so the row, its warnings, and any open diff modal all refresh
 * without a browser reload.
 */
const invalidateImports = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ["imports"] });

export const useUpdateImport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      importId: string;
      body: Partial<UpdateImportDto>;
    }) => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${vars.importId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(vars.body),
        },
      );
      await handleResponse(res);
    },
    onSuccess: (_data, vars) => {
      if (vars.body.deleted !== undefined) {
        return invalidateImportAndDownstream(queryClient);
      }
      return queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
  });
};

export const useImportRerun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importId: string) => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/diff/rerun`,
        { method: "POST" },
      );
      await handleResponse(res);
      return parseResponse(
        ImportDiffDtoSchema,
        await res.json(),
        "POST /imports/:id/diff/rerun",
      );
    },
    // A re-run creates a pending diff → the list's pending badge must update.
    onSuccess: () => invalidateImports(queryClient),
  });
};

export const useCreateImport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { force: boolean; formData: FormData }) => {
      const url = vars.force
        ? `${API_BASE}/import?force=true`
        : `${API_BASE}/import`;
      const res = await fetchWithInterceptor(url, {
        method: "POST",
        body: vars.formData,
      });
      await handleResponse(res);
      return parseResponse(
        ImportResultDtoSchema,
        await res.json(),
        "POST /import",
      );
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["imports"] }),
        queryClient.invalidateQueries({ queryKey: ["transactions"] }),
        queryClient.invalidateQueries({ queryKey: ["monthlySummary"] }),
      ]),
  });
};

export const pendingDiffsKey = (importId: string) =>
  ["imports", importId, "pendingDiffs"] as const;

export const useImportPendingDiffs = (importId: string, enabled: boolean) => {
  return useQuery<PendingImportFileDto[]>({
    queryKey: pendingDiffsKey(importId),
    queryFn: async () => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/diff`,
      );
      if (!res.ok) return [];
      return parseResponse(
        z.array(PendingImportFileDtoSchema),
        await res.json(),
        "GET /imports/:id/diff",
      );
    },
    enabled,
  });
};

export const useUploadImportDiff = (importId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/diff`,
        { method: "POST", body: formData },
      );
      await handleResponse(res);
      return parseResponse(
        ImportDiffDtoSchema,
        await res.json(),
        "POST /imports/:id/diff",
      );
    },
    onSuccess: () => invalidateImports(queryClient),
  });
};

// Like `useUploadImportDiff`, but the target import is a mutation variable rather
// than baked into the hook — lets the overlap dialog upload the picked file
// against whichever conflicting import the user chooses to update.
export const useUploadImportDiffById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      importId,
      file,
    }: {
      importId: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/diff`,
        { method: "POST", body: formData },
      );
      await handleResponse(res);
      return parseResponse(
        ImportDiffDtoSchema,
        await res.json(),
        "POST /imports/:id/diff",
      );
    },
    onSuccess: () => invalidateImports(queryClient),
  });
};

export const useLoadImportPendingDiff = (importId: string) => {
  return useMutation({
    mutationFn: async (pendingId: string) => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/diff/${pendingId}`,
      );
      await handleResponse(res);
      return parseResponse(
        ImportDiffDtoSchema,
        await res.json(),
        "GET /imports/:id/diff/:pendingId",
      );
    },
  });
};

export const useApplyImportDiff = (importId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pendingId: string) => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/apply/${pendingId}`,
        { method: "POST" },
      );
      await handleResponse(res);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: pendingDiffsKey(importId),
      });
      await invalidateImportAndDownstream(queryClient);
    },
  });
};

export const useDiscardImportDiff = (importId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pendingId: string) => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/diff/${pendingId}`,
        { method: "DELETE" },
      );
      await handleResponse(res);
    },
    onSuccess: () => invalidateImports(queryClient),
  });
};

export const useImportCsv = (importId: string | null, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ["imports", importId, "csv"],
    queryFn: async () => {
      const res = await fetchWithInterceptor(
        `${API_BASE}/imports/${importId}/csv`,
      );
      await handleResponse(res);
      return res.text();
    },
    enabled: enabled && importId !== null,
    // A stored file the server can't turn into CSV fails the same way every
    // time; show that error now rather than after three backed-off retries.
    retry: (failureCount, error) => !isClientError(error) && failureCount < 3,
  });
};

// ---------------------------------------------------------------------------
// Rule library
//
// Editing a set changes every rule citing it, and a rule can become dead when
// a set is deleted — so every library mutation invalidates rules and
// transactions too, not just the library itself.

const invalidateLibrary = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["matchSets"] });
  queryClient.invalidateQueries({ queryKey: ["actionSets"] });
  queryClient.invalidateQueries({ queryKey: ["autoTransactionRules"] });
  queryClient.invalidateQueries({ queryKey: ["transactions"] });
};

export const useMatchSets = () =>
  useQuery<MatchSetDto[]>({
    queryKey: ["matchSets"],
    queryFn: getMatchSets,
  });

export const useActionSets = () =>
  useQuery<ActionSetDto[]>({
    queryKey: ["actionSets"],
    queryFn: getActionSets,
  });

export const useCreateMatchSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (set: NewMatchSetDto) => createMatchSet(set),
    onSuccess: () => invalidateLibrary(queryClient),
  });
};

export const useUpdateMatchSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, set }: { id: string; set: UpdateMatchSetDto }) =>
      updateMatchSet(id, set),
    onSuccess: () => invalidateLibrary(queryClient),
  });
};

export const useDeleteMatchSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMatchSet(id),
    onSuccess: () => invalidateLibrary(queryClient),
  });
};

export const useCreateActionSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (set: NewActionSetDto) => createActionSet(set),
    onSuccess: () => invalidateLibrary(queryClient),
  });
};

export const useUpdateActionSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, set }: { id: string; set: UpdateActionSetDto }) =>
      updateActionSet(id, set),
    onSuccess: () => invalidateLibrary(queryClient),
  });
};

export const useDeleteActionSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteActionSet(id),
    onSuccess: () => invalidateLibrary(queryClient),
  });
};
